/**
 * Theme presets — drive the storefront colour palette + typography by
 * overriding the same CSS custom properties already used across the app
 * (--kb-primary, --kb-accent, --kb-bg, etc.). Switching `theme_preset`
 * on /admin/branding instantly re-skins every customer's experience.
 *
 * Each preset uses HSL triplets (no alpha) so existing `hsl(var(--kb-primary) / 0.06)`
 * patterns continue to work.
 *
 * `fontPairs` — curated typography pairings hand-picked for each preset.
 * The tenant picks one (or sticks with the default). Each pair declares:
 *   key      — stable identifier persisted on settings.font_pair
 *   label    — short headline shown in the picker card
 *   note     — one-liner about when this pair shines
 *   display  — value for the `font-display` class (headings, logo)
 *   body     — value for the document <body>
 *   googleFamilies — array of Google Fonts query strings to lazy-load
 */

const PAIR = (key, label, note, display, body, googleFamilies) => ({
  key, label, note, display, body, googleFamilies,
});

export const THEME_PRESETS = {
  kalki: {
    label: "Kalki — Maroon & Gold",
    description: "Editorial Indian luxury. Deep maroon, soft pearl, golden accents.",
    swatches: ["#7A1C29", "#D4AF37", "#FDFBF7"],
    vars: {
      "--kb-bg": "36 53% 98%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "38 29% 94%",
      "--kb-primary": "351 62% 29%",
      "--kb-primary-hover": "351 62% 22%",
      "--kb-text": "0 0% 10%",
      "--kb-muted": "0 0% 40%",
      "--kb-border": "35 21% 87%",
      "--kb-accent": "46 65% 52%",
      "--kb-dashboard-bg": "40 15% 97%",
    },
    fontPairs: [
      PAIR("editorial", "Editorial Luxe", "Soft serif headings paired with a clean modern sans. Our signature recommendation.",
        "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
        "'Outfit', system-ui, sans-serif",
        ["Cormorant+Garamond:wght@300;400;500;600;700", "Outfit:wght@300;400;500;600;700"]),
      PAIR("regal", "Regal Boutique", "Higher contrast Didone headings. Feels couture-store.",
        "'Playfair Display', 'Bodoni Moda', Georgia, serif",
        "'Lora', Georgia, serif",
        ["Playfair+Display:wght@400;500;600;700;800", "Lora:wght@400;500;600;700"]),
      PAIR("heritage", "Heritage", "Sharp serif for tradition, warm humanist sans for clarity.",
        "'DM Serif Display', 'Cormorant Garamond', serif",
        "'Work Sans', system-ui, sans-serif",
        ["DM+Serif+Display", "Work+Sans:wght@300;400;500;600"]),
      PAIR("modern-luxe", "Modern Luxe", "Geometric headings; clean reads for younger luxury buyers.",
        "'Cormorant Infant', 'Cormorant Garamond', serif",
        "'Manrope', system-ui, sans-serif",
        ["Cormorant+Infant:wght@400;500;600;700", "Manrope:wght@300;400;500;600;700"]),
    ],
    radius: "0.25rem",
  },

  myntra: {
    label: "Myntra — Magenta & Tangerine",
    description: "Bright, energetic e-commerce. Pink-magenta primary, orange accents, dense grid.",
    swatches: ["#FF3F6C", "#FF905A", "#FFFFFF"],
    vars: {
      "--kb-bg": "0 0% 100%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "0 0% 97%",
      "--kb-primary": "344 100% 62%",
      "--kb-primary-hover": "344 100% 52%",
      "--kb-text": "210 11% 15%",
      "--kb-muted": "210 7% 46%",
      "--kb-border": "0 0% 89%",
      "--kb-accent": "20 100% 60%",
      "--kb-dashboard-bg": "0 0% 99%",
    },
    fontPairs: [
      PAIR("commerce", "Commerce", "Friendly Poppins headlines, ultra-legible Inter body. Marketplace default.",
        "'Poppins', sans-serif", "'Inter', system-ui, sans-serif",
        ["Poppins:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("trendy", "Trendy", "Compact Plus Jakarta Sans for headers, paired with Inter.",
        "'Plus Jakarta Sans', sans-serif", "'Inter', system-ui, sans-serif",
        ["Plus+Jakarta+Sans:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("fresh", "Fresh", "Soft Manrope across everything — keeps the page calm.",
        "'Manrope', sans-serif", "'Manrope', system-ui, sans-serif",
        ["Manrope:wght@300;400;500;600;700;800"]),
      PAIR("playful", "Playful Editorial", "Plump DM Sans headlines balance the bright pink palette.",
        "'DM Sans', sans-serif", "'Inter', system-ui, sans-serif",
        ["DM+Sans:wght@400;500;700", "Inter:wght@300;400;500;600;700"]),
    ],
    radius: "0.5rem",
  },

  ajio: {
    label: "Ajio — Charcoal & Acid Lime",
    description: "Sporty modern. Near-black primary, neon-lime accent, geometric.",
    swatches: ["#171717", "#D6FF00", "#FAFAFA"],
    vars: {
      "--kb-bg": "0 0% 99%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "0 0% 95%",
      "--kb-primary": "0 0% 9%",
      "--kb-primary-hover": "0 0% 0%",
      "--kb-text": "0 0% 9%",
      "--kb-muted": "0 0% 45%",
      "--kb-border": "0 0% 88%",
      "--kb-accent": "70 100% 50%",
      "--kb-dashboard-bg": "0 0% 98%",
    },
    fontPairs: [
      PAIR("athletic", "Athletic", "Manrope everywhere — clean, geometric, sporty.",
        "'Manrope', sans-serif", "'Manrope', system-ui, sans-serif",
        ["Manrope:wght@300;400;500;600;700;800"]),
      PAIR("brutalist", "Brutalist", "Sharp Space Grotesk headers over neutral Inter body.",
        "'Space Grotesk', sans-serif", "'Inter', system-ui, sans-serif",
        ["Space+Grotesk:wght@400;500;600;700", "Inter:wght@300;400;500;600;700"]),
      PAIR("technical", "Technical", "Wide, condensed Archivo for impact + clean reading text.",
        "'Archivo', sans-serif", "'Inter', system-ui, sans-serif",
        ["Archivo:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("hype", "Hype Drop", "Heavy Anton headers; great for limited drops + flash sale banners.",
        "'Anton', 'Bebas Neue', sans-serif", "'Inter', system-ui, sans-serif",
        ["Anton", "Inter:wght@300;400;500;600;700"]),
    ],
    radius: "0.125rem",
  },

  mango: {
    label: "Mango — Pure Monochrome",
    description: "Editorial monochrome. Black on white minimalism, generous whitespace, large serif type.",
    swatches: ["#000000", "#B0B0B0", "#FFFFFF"],
    vars: {
      "--kb-bg": "0 0% 100%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "0 0% 96%",
      "--kb-primary": "0 0% 0%",
      "--kb-primary-hover": "0 0% 22%",
      "--kb-text": "0 0% 7%",
      "--kb-muted": "0 0% 45%",
      "--kb-border": "0 0% 88%",
      "--kb-accent": "0 0% 60%",
      "--kb-dashboard-bg": "0 0% 99%",
    },
    fontPairs: [
      PAIR("classic", "Classic Editorial", "Magazine-style Playfair display with Inter body.",
        "'Playfair Display', serif", "'Inter', system-ui, sans-serif",
        ["Playfair+Display:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("monoluxe", "Mono Luxe", "Tall Bodoni headlines balanced by a humanist sans.",
        "'Bodoni Moda', serif", "'Karla', system-ui, sans-serif",
        ["Bodoni+Moda:wght@400;500;600;700;800", "Karla:wght@300;400;500;600;700"]),
      PAIR("salon", "Salon", "Stylish Cormorant headings with calm Manrope reading.",
        "'Cormorant Garamond', serif", "'Manrope', system-ui, sans-serif",
        ["Cormorant+Garamond:wght@300;400;500;600;700", "Manrope:wght@300;400;500;600;700"]),
      PAIR("museum", "Museum", "Big-presence Fraunces headings with quiet Inter body.",
        "'Fraunces', serif", "'Inter', system-ui, sans-serif",
        ["Fraunces:wght@300;400;500;600;700;900", "Inter:wght@300;400;500;600;700"]),
    ],
    radius: "0",
  },

  biba: {
    label: "Biba — Coral & Cream",
    description: "Warm Indian ethnic. Coral-red primary, cream backdrop, ochre accent.",
    swatches: ["#C8473C", "#E5A33B", "#FBF4E9"],
    vars: {
      "--kb-bg": "33 67% 96%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "33 47% 90%",
      "--kb-primary": "5 60% 51%",
      "--kb-primary-hover": "5 60% 42%",
      "--kb-text": "20 14% 16%",
      "--kb-muted": "20 8% 42%",
      "--kb-border": "33 32% 84%",
      "--kb-accent": "37 73% 56%",
      "--kb-dashboard-bg": "33 50% 95%",
    },
    fontPairs: [
      PAIR("warm-classic", "Warm Classic", "Tinos heading with Lora body — book-paper warmth.",
        "'Tinos', serif", "'Lora', Georgia, serif",
        ["Tinos:wght@400;700", "Lora:wght@400;500;600;700"]),
      PAIR("market", "Market Bazaar", "Friendly Crimson Pro headers, soft Source Sans body.",
        "'Crimson Pro', serif", "'Source Sans 3', system-ui, sans-serif",
        ["Crimson+Pro:wght@400;500;600;700", "Source+Sans+3:wght@300;400;500;600"]),
      PAIR("festive", "Festive", "Decorative Frank Ruhl Libre headlines + clean Mulish reading.",
        "'Frank Ruhl Libre', serif", "'Mulish', system-ui, sans-serif",
        ["Frank+Ruhl+Libre:wght@400;500;700;900", "Mulish:wght@300;400;500;600;700"]),
      PAIR("artisan", "Artisan", "Hand-lettered feel Italiana headings + Lora body.",
        "'Italiana', serif", "'Lora', Georgia, serif",
        ["Italiana", "Lora:wght@400;500;600;700"]),
    ],
    radius: "0.25rem",
  },

  neerus: {
    label: "Neerus — Emerald & Champagne",
    description: "Couture jewel. Deep emerald primary, champagne gold accent, ornate type.",
    swatches: ["#19543E", "#C9A86B", "#FAF7EE"],
    vars: {
      "--kb-bg": "50 41% 96%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "50 30% 92%",
      "--kb-primary": "156 53% 22%",
      "--kb-primary-hover": "156 53% 16%",
      "--kb-text": "165 22% 12%",
      "--kb-muted": "165 8% 38%",
      "--kb-border": "50 18% 82%",
      "--kb-accent": "39 41% 60%",
      "--kb-dashboard-bg": "50 35% 95%",
    },
    fontPairs: [
      PAIR("couture", "Couture", "All-caps Cinzel headers, soft Cormorant Garamond body.",
        "'Cinzel', serif", "'Cormorant Garamond', Georgia, serif",
        ["Cinzel:wght@400;500;600;700", "Cormorant+Garamond:wght@300;400;500;600;700"]),
      PAIR("opera", "Opera", "Tall Cormorant SC headings paired with refined Cormorant body.",
        "'Cormorant SC', serif", "'Cormorant Garamond', Georgia, serif",
        ["Cormorant+SC:wght@400;500;600;700", "Cormorant+Garamond:wght@300;400;500;600;700"]),
      PAIR("regalia", "Regalia", "Elegant Marcellus titles balanced by warm Lora body.",
        "'Marcellus', serif", "'Lora', Georgia, serif",
        ["Marcellus", "Lora:wght@400;500;600;700"]),
      PAIR("haute", "Haute Couture", "Higher contrast Cinzel + Crimson Pro for editorial reads.",
        "'Cinzel', serif", "'Crimson Pro', Georgia, serif",
        ["Cinzel:wght@400;500;600;700", "Crimson+Pro:wght@400;500;600;700"]),
    ],
    radius: "0.125rem",
  },
};

export const DEFAULT_PRESET = "kalki";

export function getPreset(key) {
  return THEME_PRESETS[key] || THEME_PRESETS[DEFAULT_PRESET];
}

// ---------------------------------------------------------------------------
// Colour helpers — bridge between the HSL-triplet form preset CSS vars use
// ("0 0% 9%") and the hex strings the tenant interacts with in the colour
// picker (#171717). Keep both directions deterministic + lossless within the
// 8-bit RGB range we care about.
// ---------------------------------------------------------------------------

function _hueToRgb(p, q, t) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

/** Parse "H S% L%" CSS-triplet form to {h, s, l} (0-360, 0-1, 0-1). */
function parseHslTriplet(triplet) {
  if (!triplet || typeof triplet !== "string") return null;
  const m = triplet.trim().match(/^(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
  if (!m) return null;
  return { h: +m[1], s: +m[2] / 100, l: +m[3] / 100 };
}

/** Convert a CSS HSL triplet ("0 0% 9%") to a `#rrggbb` string. */
export function hslTripletToHex(triplet) {
  const hsl = parseHslTriplet(triplet);
  if (!hsl) return null;
  const { h, s, l } = hsl;
  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hk = h / 360;
    r = _hueToRgb(p, q, hk + 1 / 3);
    g = _hueToRgb(p, q, hk);
    b = _hueToRgb(p, q, hk - 1 / 3);
  }
  const to2 = (v) => Math.round(v * 255).toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/** Convert `#rrggbb` to the CSS-triplet form preset vars use ("0 0% 9%"). */
export function hexToHslTriplet(hex) {
  if (!hex || typeof hex !== "string") return null;
  const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const intVal = parseInt(m[1], 16);
  const r = ((intVal >> 16) & 255) / 255;
  const g = ((intVal >> 8) & 255) / 255;
  const b = (intVal & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h;
  let s;
  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
  }
  // Round to 0 decimal for hue, 0 for percentages (matches preset format)
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Return the four overridable storefront colours for a preset, as hex.
 * Drives the colour picker auto-population on `/admin/branding`.
 */
export function getPresetDefaultColors(presetKey) {
  const preset = getPreset(presetKey);
  const v = preset.vars || {};
  return {
    primary_color: hslTripletToHex(v["--kb-primary"]) || "#000000",
    accent_color: hslTripletToHex(v["--kb-accent"]) || "#000000",
    background_color: hslTripletToHex(v["--kb-bg"]) || "#ffffff",
    surface_color: hslTripletToHex(v["--kb-surface"]) || "#ffffff",
  };
}

/**
 * Returns the font pair the tenant has selected for the active preset,
 * or the preset's first pair (= recommended default) when unset.
 */
export function getFontPair(presetKey, pairKey) {
  const preset = getPreset(presetKey);
  const pairs = preset.fontPairs || [];
  if (!pairs.length) return null;
  if (pairKey) {
    const match = pairs.find((p) => p.key === pairKey);
    if (match) return match;
  }
  return pairs[0];
}
